import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { formatTime } from "../race/PacingTable";

/**
 * PRTiles — grid of best times per canonical distance.
 *
 * Pure presentational. Caller hands us the array from
 * /athlete/personal-records. Empty list → empty card.
 *
 * Each tile shows:
 *   - distance label (5K / 10K / Half / Marathon)
 *   - best time (M:SS or H:MM:SS via formatTime)
 *   - delta vs 90-day average (positive = recent slower → red,
 *     negative = improved → green)
 *   - "stale" muted treatment when older than 365 days
 */

export interface PR {
  key: string;
  distance_km: number;
  best_time_seconds: number;
  best_workout_id: number | null;
  achieved_at: string | null;
  delta_vs_90d_seconds: number | null;
  is_stale: boolean;
  discipline?: "run" | "swim" | "ride";
  metric?: "time" | "power";
  best_power_watts?: number | null;
}

const DISTANCE_LABELS: Record<string, string> = {
  "5k": "5K",
  "10k": "10K",
  half: "Half",
  marathon: "Marathon",
  // Power-рекорды: без словаря ключ печатался сырым («POWER_5MIN»).
  power_5min: "Мощность 5′",
  power_20min: "Мощность 20′",
};

/** Power-рекорд узнаём и по метрике, и по ключу: старые ответы бэкенда
 *  metric не заполняли. */
function isPowerPR(pr: PR): boolean {
  return pr.metric === "power" || pr.key.startsWith("power_");
}

export interface PRTilesProps {
  records: PR[];
  className?: string;
}

function formatAchievedDate(iso: string): string {
  // Compact dd.mm.yy
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y.slice(2)}`;
}

function formatDelta(seconds: number): string {
  const sign = seconds > 0 ? "+" : "−";
  const abs = Math.abs(seconds);
  if (abs >= 60) {
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return `${sign}${m}:${String(s).padStart(2, "0")}`;
  }
  return `${sign}${abs}s`;
}

export function PRTiles({ records, className = "" }: PRTilesProps) {
  const { t } = useTranslation("plan");

  if (records.length === 0) {
    return (
      <div
        data-testid="pr-tiles-empty"
        className={`indigo-card p-4 ${className}`}
      >
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {t("pr_tiles.title", { defaultValue: "Личные рекорды" })}
        </div>
        <div className="mt-2 text-xs text-fg-muted">
          {t("pr_tiles.empty", { defaultValue: "Пробеги одну из канонических дистанций — рекорд появится сам" })}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="pr-tiles"
      className={`indigo-card p-4 ${className}`}
    >
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {t("pr_tiles.title", { defaultValue: "Личные рекорды" })}
        </span>
        <span className="text-[9px] uppercase tracking-[0.18em] text-fg-muted font-bold tabular-nums">
          {/* «дистанций» врало для power-рекордов — 5′/20′ не дистанция */}
          {t("pr_tiles.count", { defaultValue: "рекордов: {{n}}", n: records.length })}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {records.map((pr) => {
          const tone = pr.is_stale ? "stale" : "fresh";
          const deltaTone =
            pr.delta_vs_90d_seconds == null
              ? "neutral"
              : pr.delta_vs_90d_seconds > 0
              ? "improved" // delta > 0 means recent avg slower; best is improvement
              : "regressed";
          return (
            <Link
              key={pr.key}
              to={`/pr/${pr.key}`}
              data-testid={`pr-tile-${pr.key}`}
              data-tone={tone}
              aria-label={`${t(`dashboard:race_labels.${pr.key}`, { defaultValue: DISTANCE_LABELS[pr.key] ?? pr.key })} · открыть детали рекорда`}
              className={`block rounded-md border px-3 py-2.5 transition-colors hover:bg-bg-raised ${
                pr.is_stale
                  ? "border-bg-border/40 bg-bg-raised/30"
                  : "border-bg-border bg-bg-raised/60"
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-fg-muted font-bold">
                {/* race-дистанция — через канонический словарь race_labels,
                    не сырой enum (HALF-инцидент) */}
                {t(`dashboard:race_labels.${pr.key}`, { defaultValue: DISTANCE_LABELS[pr.key] ?? pr.key })}
              </div>
              <div
                className={`mt-1 text-base font-bold tabular-nums leading-none ${
                  pr.is_stale ? "text-fg-secondary" : "text-fg-primary"
                }`}
              >
                {/* Мощность — в ваттах; «0:00» для power-рекорда было
                    временем, которого у ватт не бывает. Нет данных — «—». */}
                {isPowerPR(pr)
                  ? pr.best_power_watts != null && pr.best_power_watts > 0
                    ? `${Math.round(pr.best_power_watts)} ${t("pr_tiles.watts", { defaultValue: "Вт" })}`
                    : "—"
                  : pr.best_time_seconds > 0
                    ? formatTime(pr.best_time_seconds)
                    : "—"}
              </div>
              {pr.achieved_at && (
                <div className="mt-1 text-[8.5px] uppercase tracking-[0.16em] text-fg-muted font-semibold tabular-nums">
                  {formatAchievedDate(pr.achieved_at)}
                </div>
              )}
              {pr.delta_vs_90d_seconds != null && (
                <div
                  data-testid={`pr-tile-${pr.key}-delta`}
                  data-delta-tone={deltaTone}
                  className={`mt-1 text-[9px] uppercase tracking-[0.14em] font-bold tabular-nums ${
                    deltaTone === "improved"
                      ? "text-ok"
                      : deltaTone === "regressed"
                      ? "text-warn"
                      : "text-fg-muted"
                  }`}
                >
                  {formatDelta(pr.delta_vs_90d_seconds)}
                </div>
              )}
              {pr.is_stale && (
                <div className="mt-1 text-[8.5px] uppercase tracking-[0.16em] text-fg-muted font-semibold">
                  {t("pr_tiles.stale", { defaultValue: "Можно побить" })}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
